import React, { Component, Fragment } from "react";
import { Query } from "react-apollo";
import gql from "graphql-tag";
import Linkify from "react-linkify";
import parse from "html-react-parser";
import { Link as LinkTo } from "react-router-dom";
import {
  timeDifferenceForDate,
  repliesFragmentForCounting,
  commentFormatter,
} from "../utils";
import Link from "./Link";
import Comment from "./Comment";
import Reply from "./Reply";
import { USER_ID } from "../constants";

export const USER_LINKS_QUERY = gql`
  query UserLinksQuery($userId: ID!) {
    userFeed(userId: $userId) {
      links {
        id
        created
        url
        description
        ranking
        postedBy {
          id
          name
        }
        votes {
          id
        }
        comments {
          id
          ${repliesFragmentForCounting}
        }
      }
    }
  }
`;

export const PROFILE_QUERY = gql`
  query ProfileQuery($userId: ID!) {
    profile(userId: $userId) {
      name
      bio
      created
      notifications
      links {
        votes {
          id
        }
      }
    }
  }
`;

export const USER_COMMENTS_QUERY = gql`
  query UserCommentsQuery($userId: ID!) {
    userComments(userId: $userId) {
      comments {
        id
        text
        created
        link {
          id
          description
        }
        user {
          id
          name
        }
        replies {
          id
        }
      }
      replies {
        id
        text
        created
        comment {
          id
          text
        }
        reply {
          id
          text
        }
        user {
          id
          name
        }
        replies {
          id
        }
      }
    }
  }
`;

class Account extends Component {
  _updateCacheAfterDelete = (store, linkData, linkId) => {
    try {
      const userLinksData = store.readQuery({
        query: USER_LINKS_QUERY,
        variables: { userId: this.props.match.params.id },
      });
      userLinksData.userFeed.links = userLinksData.userFeed.links.filter(
        (link) => link.id !== linkId
      );

      store.writeQuery({
        query: USER_LINKS_QUERY,
        variables: { userId: this.props.match.params.id },
        data: userLinksData,
      });
    } catch (err) {
      console.log("No user links query in cache to update");
    }

    try {
      // Invalidate the cached feed queries
      Object.keys(store.data.data).forEach((key) => {
        key.match(/ROOT_QUERY.feed/) && store.data.delete(key);
      });
    } catch (err) {
      console.log("No feed query in cache to invalidate");
    }
  };

  render() {
    return (
      <Fragment>
        <Query
          query={PROFILE_QUERY}
          variables={{ userId: this.props.match.params.id }}
        >
          {({ loading, error, data }) => {
            if (error)
              return (
                <div className="mt2">
                  An error occured when fetching user data...
                </div>
              );
            if (loading) return "";

            return (
              <Fragment>
                <div className="flex">
                  <div className="sidebar label gray pt2 pr2">user:</div>
                  <div className="pt2 gray">{data.profile.name}</div>
                </div>
                <div className="flex">
                  <div className="sidebar label gray pt2 pr2">created:</div>
                  <div className="pt2 gray">
                    {timeDifferenceForDate(data.profile.created)}
                  </div>
                </div>
                {data.profile.links && (
                  <div className="flex">
                    <div className="sidebar label gray pt2 pr2">karma:</div>
                    <div className="pt2 gray">
                      {data.profile.links.reduce((acc, link) => {
                        return acc + link.votes.length;
                      }, 0)}
                    </div>
                  </div>
                )}
                {data.profile.bio && (
                  <div className="flex">
                    <div className="sidebar label gray pt2 pr2">bio:</div>
                    <div className="pt2 gray respect-lb">
                      <Linkify>
                        {parse(commentFormatter(data.profile.bio))}
                      </Linkify>
                    </div>
                  </div>
                )}
                {typeof data.profile.notifications === "boolean" &&
                  localStorage.getItem(USER_ID) ===
                  this.props.match.params.id &&
                  (
                    <div className="flex pt1">
                      <div className="sidebar label gray pr2 pt1">emails:</div>
                      <div className="notification-settings-container pt2">
                        <div>
                          <input className="mr1" type="checkbox" id="subscribePostComments" name="subscribe" disabled={true} checked={data.profile.notifications}>
                          </input>
                          <label className="gray">Email notification on replies</label>
                        </div>
                      </div>
                    </div>
                  )}
                {localStorage.getItem(USER_ID) ===
                  this.props.match.params.id && (
                    <LinkTo to="/editaccount">
                      <button type="submit" className="pointer mt2 mb3 button">
                        edit
                    </button>
                    </LinkTo>
                  )}
              </Fragment>
            );
          }}
        </Query>
        <div className="flex">
          <div className="sidebar label gray pt2 pr2">submissions:</div>
          <div>
            <Query
              query={USER_LINKS_QUERY}
              variables={{ userId: this.props.match.params.id }}
            >
              {({ loading, error, data }) => {
                if (loading)
                  return <div className="pt2 animate-flicker">Loading...</div>;
                if (error) {
                  return <div className="pt2">Error fetching submissions</div>;
                }

                return (
                  <Fragment>
                    {data.userFeed.links.length ? (
                      data.userFeed.links.map((link, index) => (
                        <Link
                          key={link.id}
                          index={index}
                          link={link}
                          deletable={
                            localStorage.getItem(USER_ID) ===
                            this.props.match.params.id
                          }
                          updateStoreAfterDelete={this._updateCacheAfterDelete}
                          votable={false}
                          forAccountPage={true}
                        />
                      ))
                    ) : (
                        <div className="pt2 gray">No submissions yet</div>
                      )}
                  </Fragment>
                );
              }}
            </Query>
          </div>
        </div>
        <div className="flex pt4">
          <div className="sidebar label gray pt2 pr2">comments:</div>
          <div>
            <Query
              query={USER_COMMENTS_QUERY}
              variables={{ userId: this.props.match.params.id }}
            >
              {({ loading, error, data }) => {
                if (loading)
                  return <div className="pt2 animate-flicker">Loading...</div>;
                if (error) {
                  return (
                    <div className="pt2 gray">Error fetching comments</div>
                  );
                }
                const comments = data.userComments
                  ? [
                    ...data.userComments.comments,
                    ...data.userComments.replies.filter(
                      (reply) => reply.comment || reply.reply
                    ),
                  ]
                  : [];
                return (
                  <Fragment>
                    {comments.length ? (
                      comments
                        .sort(
                          (a, b) => new Date(b.created) - new Date(a.created)
                        )
                        .map((reply, index, all) => {
                          if (reply.__typename === "Comment") {
                            return (
                              <Comment
                                key={reply.id}
                                forAccountPage={true}
                                parent={reply.link}
                                id={reply.id}
                                text={reply.text}
                                user={reply.user}
                                created={reply.created}
                                replies={reply.replies}
                              />
                            );
                          } else {
                            return (
                              <Reply
                                key={reply.id}
                                forAccountPage={true}
                                parent={reply.comment || reply.reply}
                                id={reply.id}
                                text={reply.text}
                                user={reply.user}
                                created={reply.created}
                                replies={reply.replies}
                              />
                            );
                          }
                        })
                    ) : (
                        <div className="pt2 gray">No comments yet</div>
                      )}
                  </Fragment>
                );
              }}
            </Query>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default Account;



// WEBPACK FOOTER //
// ./src/components/Account.js